// Copying a custom view: the view row and its data sources, never its asset links (a copy shouldn't assume which
// assets it belongs to). Pure, so the modal, the thunk and the Jest cases share one copy of the rules.

import { resolveForecastOwnership, type ForecastOwnership, type OrganizationSummary } from "./composedForecastOwnership";

// The view settings a copy carries over. Runtime state (lastProcessed) and identity (id, timestamps) start fresh.
const FORECAST_COPY_FIELDS = [
  "description",
  "forecastType",
  "aggregationMethod",
  "updateFrequency",
  "forecastHorizon",
  "outputParameters",
  "isActive",
  "processingSchedule",
  "validationThresholds",
] as const;

// The source settings a copy carries over: what it reads and how. Fetch status and quality score belong to the
// original's runs, so the copy starts without them.
const DATA_SOURCE_COPY_FIELDS = [
  "sourceType",
  "sourceName",
  "sourceDescription",
  "processedModelOutputId",
  "postgresTable",
  "postgresColumns",
  "postgresAggregation",
  "weight",
  "priority",
  "timeAlignment",
  "temporalResolution",
  "fallbackStrategy",
  "isActive",
  "metadata",
  "dataOperations",
] as const;

const AWSJSON_FIELDS = new Set(["outputParameters", "processingSchedule", "validationThresholds", "metadata", "dataOperations"]);

// AppSync takes AWSJSON as a string; the list read can hand it back parsed in some resolver shapes
const toInputValue = (key: string, value: unknown) =>
  AWSJSON_FIELDS.has(key) && value !== null && typeof value === "object" ? JSON.stringify(value) : value;

function pickFields(source: Record<string, any>, fields: readonly string[]): Record<string, any> {
  const picked: Record<string, any> = {};
  for (const key of fields) {
    const value = source?.[key];
    if (value !== undefined && value !== null) picked[key] = toInputValue(key, value);
  }
  return picked;
}

export const defaultCopyName = (name?: string | null): string => `Duplicate of ${name || "custom view"}`;

/**
 * Who owns the copy. Kept on the original's org, it keeps the original's owner, so the same people can manage it.
 * Moved to another org, it follows the create rule: an Admin hands it to that org's owner, otherwise the caller
 * owns it (no owner in the input = AppSync stamps the caller).
 */
export function resolveCopyOwnership(
  original: { owner?: string | null; organizationId?: string | null },
  selectedOrganizationId: string,
  organizations: Record<string, OrganizationSummary>,
  isAdmin: boolean
): ForecastOwnership {
  const sameOrg = (selectedOrganizationId || null) === (original.organizationId || null);
  return resolveForecastOwnership({
    selectedOrganizationId,
    organizations,
    isAdmin,
    existing: sameOrg ? original : undefined,
  });
}

export function buildCopyForecastInput(
  original: Record<string, any>,
  name: string,
  ownership: ForecastOwnership
): Record<string, any> {
  return {
    ...pickFields(original, FORECAST_COPY_FIELDS),
    name: name.trim(),
    ...(ownership.owner ? { owner: ownership.owner } : {}),
    organizationId: ownership.organizationId,
  };
}

export function buildCopyDataSourceInput(
  source: Record<string, any>,
  composedForecastId: string,
  owner: string | null | undefined,
  organizationId: string | null
): Record<string, any> {
  return {
    ...pickFields(source, DATA_SOURCE_COPY_FIELDS),
    composedForecastId,
    ...(owner ? { owner } : {}),
    organizationId,
  };
}

export type CopyPhase = "view" | "sources" | "rollback" | "done" | "failed";

export interface CopyProgress {
  phase: CopyPhase;
  /** Data sources copied so far */
  sourcesDone: number;
  sourcesTotal: number;
  /** Set on failure: the step that broke and what went wrong */
  failedAt?: CopyPhase;
  error?: string;
  /** Set once an undo ran (only when something had been created): whether the partial copy was removed */
  rolledBack?: boolean;
}

export type CopyStepStatus = "pending" | "running" | "done" | "failed";

export interface CopyStep {
  key: string;
  label: string;
  status: CopyStepStatus;
}

const PHASE_ORDER: CopyPhase[] = ["view", "sources", "done"];

/** The progress list the copy modal shows: one row per step, plus an undo row once a failure triggers one. */
export function describeCopySteps(progress: CopyProgress): CopyStep[] {
  const { phase, sourcesDone, sourcesTotal, failedAt } = progress;
  // Where the copy got to: on failure that is the step that broke, otherwise the live phase
  const reached = phase === "failed" || phase === "rollback" ? failedAt ?? "view" : phase;
  const reachedIndex = PHASE_ORDER.indexOf(reached);
  const failed = phase === "failed" || phase === "rollback";
  const statusOf = (step: CopyPhase): CopyStepStatus => {
    const index = PHASE_ORDER.indexOf(step);
    if (index < reachedIndex) return "done";
    if (index > reachedIndex) return "pending";
    return failed ? "failed" : reached === "done" ? "done" : "running";
  };

  const steps: CopyStep[] = [
    { key: "view", label: "Create the view", status: statusOf("view") },
    {
      key: "sources",
      label:
        sourcesTotal === 0
          ? "No data sources to duplicate"
          : `Duplicate data sources (${sourcesDone} of ${sourcesTotal})`,
      status: statusOf("sources"),
    },
  ];

  if (phase === "rollback") steps.push({ key: "rollback", label: "Undoing the partial duplicate", status: "running" });
  if (phase === "failed" && progress.rolledBack !== undefined) {
    steps.push({
      key: "rollback",
      label: progress.rolledBack ? "Removed the partial duplicate" : "Couldn't remove the partial duplicate",
      status: progress.rolledBack ? "done" : "failed",
    });
  }
  return steps;
}
